import { nextClientCastSequence } from '../systems/combat/ClientCastSequence';
/**
 * __vqa namespace — dev-only entry points for the Visual QA arc.
 *
 * Installed only when URL includes `?vqa=1`. Never leaks into prod
 * because the install path is gated by URL param read at boot time, and
 * the namespace name starts with `__` so it's greppable as obviously-dev
 * surface.
 *
 * Cross-ecosystem precedent per DJ's VQA arc Phase 2 plan: this pattern
 * should be followable by H3D / DJ-Engine for their own VQA arcs.
 *
 * See docs/plans/2026-04-17-VQA-ARC-PLAN.md §Unknowns #4.
 */

import { gameLogger } from '../../utils/GameLogger';
import { bootSearchParams } from '../../config/boot/bootUrl';

export type VqaCastOptions = {
  target_id?: string;
  target_x?: number;
  target_y?: number;
};

export interface VqaNamespace {
  /** Fire an ability through the authoritative `castAbility` message path. */
  useAbility(abilityId: string, opts?: VqaCastOptions): void;
  /**
   * Hide in-game HUD overlays (Phaser-drawn). POC stub — J.4 expands to
   * actually hide mob HP bars, coord labels, etc.
   */
  hideHud(on: boolean): void;
  /**
   * Reset per-capture state (cooldowns, buffs, projectiles in flight,
   * camera shake). POC stub — J.5 implements when per-test browser
   * reuse starts mattering (374 abilities don't fit in 374 contexts).
   */
  resetCaptureState(): void;
  /**
   * Freeze Phaser's main clock so animations stop at the captured
   * frame. POC stub — revisit in J.4 only if frame-to-frame jitter
   * makes diffs unreliable.
   */
  freezeClock(on: boolean): void;
}

declare global {
  interface Window {
    __vqa?: VqaNamespace;
  }
}

function isVqaEnabled(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const sp = bootSearchParams();
    return sp.get('vqa') === '1';
  } catch {
    return false;
  }
}

export function installVqaHook(): void {
  if (!isVqaEnabled()) return;
  if (typeof window === 'undefined') return;

  const hud = { hidden: false };

  const namespace: VqaNamespace = {
    useAbility(abilityId, opts) {
      const room = (window as any).nm?.getRoom?.();
      if (!room) {
        throw new Error('[__vqa] NetworkManager room not ready — did directBoot complete?');
      }
      room.send('castAbility', {
        ability_id: abilityId,
        client_cast_seq: nextClientCastSequence(),
        ...(opts ?? {}),
      });
    },
    hideHud(on) {
      hud.hidden = on;
      // POC stub: actual Phaser HUD hide lands in J.4. For POC we rely
      // on proof-helper's React overlay suppression + the fact that the
      // Phaser-drawn mob HP bars are small and don't dominate the frame.
    },
    resetCaptureState() {
      // POC stub. J.5 will flesh out.
    },
    freezeClock(_on) {
      // POC stub. J.4 may add `scene.time.now` freeze via debug hook.
    },
  };

  window.__vqa = namespace;
  gameLogger.info('boot', '[__vqa] installed — dev namespace exposed via ?vqa=1');
}
